import React, { FC, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import NotificationBellIcon from "assets/icons/notifications.svg?react";
import EllipseIcon from "assets/icons/ellipse.svg?react";
import RightArrowIcon from "assets/icons/arrowhead right.svg?react";
import useClickOutside from "hooks/useClickOutside";
import { useNavigate } from "react-router-dom";
import t from "utils/i18n";

export type NotificationType = {
  guid: string;
  time: string;
  title: string;
  isEllipse?: boolean;
};

interface NotificationProps {
  notifications: NotificationType[];
}

const Notification: FC<NotificationProps> = ({ notifications = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [readStatus, setReadStatus] = useState<{ [id: string]: boolean }>({});

  const NotificationPanelRef = useRef<HTMLDivElement>(null);
  const bellIconRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useClickOutside([NotificationPanelRef, bellIconRef], () => setIsOpen(false));

  // Load saved readStatus from sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem("readStatus");
    if (saved) {
      setReadStatus(JSON.parse(saved));
    }
  }, []);

  // Update readStatus whenever notifications change (keep new ones unread)
  useEffect(() => {
    setReadStatus((prev) => {
      const updated = { ...prev };
      for (const n of notifications) {
        if (!(n.guid in updated)) {
          updated[n.guid] = false;
        }
      }
      return updated;
    });
  }, [notifications]);

  // Save to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("readStatus", JSON.stringify(readStatus));
  }, [readStatus]);

  const markAllAsRead = () => {
    const allRead = notifications.reduce(
      (acc, cur) => {
        acc[cur.guid] = true;
        return acc;
      },
      {} as { [id: string]: boolean },
    );
    setReadStatus(allRead);
  };

  const allRead = notifications.every((n) => readStatus[n.guid]);
  const unreadCount = notifications.filter((n) => !readStatus[n.guid]).length;

  const Content: React.FC<NotificationType> = ({
    title,
    time,
    guid,
    isEllipse,
  }) => {
    const isClickable = guid !== "none";
    const handleClick = () => {
      if (isClickable && !readStatus[guid]) {
        setReadStatus((prev) => ({ ...prev, [guid]: true }));
      }
    };

    return (
      <div
        className={`flex flex-row items-center w-full gap-2 p-3 select-none ${
          isClickable ? "cursor-pointer hover:bg-elevation-2" : ""
        }`}
        onClick={isClickable ? handleClick : undefined}
      >
        <span className="w-3 h-3 flex items-center justify-center">
          <span
            className={readStatus[guid] || !isEllipse ? "invisible" : "visible"}
          >
            <EllipseIcon />
          </span>
        </span>
        <div className="flex flex-col ml-2">
          <h1 className={readStatus[guid] ? "font-normal" : "font-semibold"}>
            {title}
          </h1>
          <span className="text-xs text-light-soft-elevation-02">{time}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Bell Icon */}
      <div
        ref={bellIconRef}
        className="p-2 text-xs cursor-pointer select-none hover:bg-elevation-2"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
      >
        <NotificationBellIcon />

        <span className="absolute top-0 flex items-center justify-center w-5 h-5 font-semibold text-white rounded-full right-1 bg-red">
          {unreadCount > 0 ? unreadCount : "0"}
        </span>
      </div>

      {/* Notification Panel */}
      {isOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 backdrop-blur-sm top-11"
            style={{ zIndex: 99999 }}
          >
            <main
              ref={NotificationPanelRef}
              className="absolute divide-y-2 divide-elevation-2 right-6 w-[300px] rounded-md shadow-md bg-elevation-1 text-base top-1"
              style={{ zIndex: 99999 }}
            >
              <div className="flex items-center justify-between px-3 py-4">
                <span className="font-black">{t("notification.heading")}</span>
                <button
                  className={`font-bold ${
                    allRead
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-interactive-blue cursor-pointer"
                  }`}
                  disabled={allRead}
                  onClick={markAllAsRead}
                >
                  {t("notification.markAllAsRead")}
                </button>
              </div>

              <div className="flex flex-col overflow-y-auto max-h-80">
                {notifications.length === 0 ? (
                  <Content
                    guid="none"
                    title={t("notification.empty")}
                    time=""
                    isEllipse={false}
                  />
                ) : (
                  notifications.map((n, i) => (
                    <Content isEllipse={true} key={n.guid + i} {...n} />
                  ))
                )}
              </div>

              <div className="flex flex-row items-center justify-start py-3 select-none text-interactive-blue hover:bg-elevation-2 px-7 hover:cursor-pointer">
                <button
                  onClick={() => {
                    navigate("/activity");
                    setIsOpen(false);
                  }}
                >
                  {t("notification.readAll")}
                </button>
                <RightArrowIcon className="w-5" />
              </div>
            </main>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Notification;
